import { omit } from 'lodash-es'

import { RENTAL_PORTAL_MOCK_STATE } from '../../../constants/rentalPortalMockState'
import { hasRentalPortalMockState } from '../../../utils/rentalPortalMockState'
import { APPLICANT_STAGE } from '../RentalPortalApplicant.constants'

import { getMockMatchInformationText } from './RentalPortalApplicantMatchText.mocks'

// TODO: RP-10 read #1 does not exist yet (synthesises `MatchEvaluationView`)
const MOCK_MATCH_WEIGHTS = {
  normalised: {
    budget: 0.25,
    household: 0.2,
    lease_term: 0.05,
    move_in: 0.3,
    needs: 0.15,
    rooms_area: 0.05,
  },
  raw: {
    budget: 25,
    household: 20,
    lease_term: 5,
    move_in: 30,
    needs: 15,
    rooms_area: 5,
  },
}

const MOCK_MATCH_STALE_DURATION = 6000

const MOCK_MATCH_STALE_SINCE = new Map()

const MOCK_MATCH_LISTING_BUDGET = 1290

const MOCK_MATCH_LISTING_HOUSEHOLD = 3

const MOCK_MATCH_LISTING_NEEDS = ['balcony', 'lift']

const MOCK_MATCH_MOVE_IN_DATE = [
  '2026-10-20',
  '2026-11-01',
  '2026-12-05',
  '2026-10-15',
]

const MOCK_MATCH_MOVE_IN_SCORE = [0.98, 0.9605, 0.71, 1]

const MOCK_MATCH_BUDGET_STEP = 90

const MOCK_MATCH_BUDGET_STEP_COUNT = 6

const MOCK_MATCH_BUDGET_MIN = 1120

const MOCK_MATCH_SEED_FACTOR = 2654435761

const MOCK_MATCH_SEED_RANGE = 997

function getMockMatchSeed(id) {
  return ((Number(id) || 0) * MOCK_MATCH_SEED_FACTOR) % MOCK_MATCH_SEED_RANGE
}

function getMockMatchBudgetValue(seed) {
  return (
    MOCK_MATCH_BUDGET_MIN +
    (seed % MOCK_MATCH_BUDGET_STEP_COUNT) * MOCK_MATCH_BUDGET_STEP
  )
}

function getMockMatchNeedsValue(seed) {
  return seed % 3 === 0
    ? MOCK_MATCH_LISTING_NEEDS
    : MOCK_MATCH_LISTING_NEEDS.slice(0, 1)
}

function getMockMatchCriteria({ id, stage, variant }) {
  const isIncomeDeferred = stage === APPLICANT_STAGE.pool

  const seed = getMockMatchSeed(id)

  const moveInIndex = seed % MOCK_MATCH_MOVE_IN_DATE.length

  const budgetValue = getMockMatchBudgetValue(seed)

  const isBudgetCovered = budgetValue >= MOCK_MATCH_LISTING_BUDGET

  const householdValue =
    variant === 'not_eligible'
      ? MOCK_MATCH_LISTING_HOUSEHOLD + 2
      : 1 + (seed % 3)

  const needsValue = getMockMatchNeedsValue(seed)

  return [
    {
      applicant_field: 'move_in_date',
      applicant_value: MOCK_MATCH_MOVE_IN_DATE[moveInIndex],
      gates_eligibility: false,
      key: 'move_in',
      listing_value: '2026-10-15',
      provenance: { at: '2026-08-30T09:12:00Z', source: 'applicant' },
      reason: null,
      s: MOCK_MATCH_MOVE_IN_SCORE[moveInIndex],
      status: MOCK_MATCH_MOVE_IN_SCORE[moveInIndex] === 1 ? 'fits' : 'partial',
      value_type: 'date',
      weight: 30,
    },
    {
      applicant_field: 'household_size',
      applicant_value: householdValue,
      gates_eligibility: true,
      key: 'household',
      listing_value: MOCK_MATCH_LISTING_HOUSEHOLD,
      provenance: { at: '2026-08-29T16:40:00Z', source: 'manual' },
      reason: variant === 'not_eligible' ? 'household_exceeds_max' : null,
      s: variant === 'not_eligible' ? 0 : 1,
      status: variant === 'not_eligible' ? 'does_not_fit' : 'fits',
      weight: 20,
    },
    {
      applicant_field: 'max_budget_warm',
      applicant_value: variant === 'incomplete' ? null : budgetValue,
      gates_eligibility: false,
      key: 'budget',
      listing_value: MOCK_MATCH_LISTING_BUDGET,
      provenance: { at: '2026-08-30T09:12:00Z', source: 'applicant' },
      reason: null,
      s:
        variant === 'incomplete'
          ? null
          : Math.min(budgetValue / MOCK_MATCH_LISTING_BUDGET, 1),
      status:
        variant === 'incomplete'
          ? 'not_provided'
          : isBudgetCovered
            ? 'fits'
            : 'partial',
      value_type: 'money',
      weight: 25,
    },
    {
      applicant_field: 'pets',
      applicant_value: variant === 'incomplete' ? null : 'none',
      gates_eligibility: true,
      key: 'pets',
      listing_value: 'not_allowed',
      provenance: { at: '2026-08-30T09:12:00Z', source: 'applicant' },
      reason: null,
      s: null,
      status: variant === 'incomplete' ? 'not_provided' : 'fits',
      value_type: 'enum',
      weight: 0,
    },
    {
      applicant_field: 'wbs',
      applicant_value: null,
      gates_eligibility: true,
      key: 'wbs',
      listing_value: 'not_required',
      provenance: null,
      reason: 'listing_no_requirement',
      s: null,
      status: 'not_applicable',
      value_type: 'enum',
      weight: 0,
    },
    {
      applicant_field: 'needs',
      applicant_value: variant === 'incomplete' ? null : needsValue,
      gates_eligibility: false,
      key: 'needs',
      listing_value: MOCK_MATCH_LISTING_NEEDS,
      provenance: null,
      reason: null,
      s:
        variant === 'incomplete'
          ? null
          : needsValue.length / MOCK_MATCH_LISTING_NEEDS.length,
      status:
        variant === 'incomplete'
          ? 'not_provided'
          : needsValue.length === MOCK_MATCH_LISTING_NEEDS.length
            ? 'fits'
            : 'partial',
      value_type: 'list',
      weight: 15,
    },
    {
      applicant_field: 'rooms_area',
      applicant_value: null,
      gates_eligibility: false,
      key: 'rooms_area',
      listing_value: '3 rooms · 88 m²',
      provenance: null,
      reason: null,
      s: null,
      status: 'not_provided',
      weight: 5,
    },
    {
      applicant_field: 'intended_duration',
      applicant_value: null,
      gates_eligibility: false,
      key: 'lease_term',
      listing_value: 12,
      provenance: null,
      reason: null,
      s: null,
      status: 'not_provided',
      value_type: 'duration_months',
      weight: 5,
    },
    {
      applicant_field: 'income_min',
      applicant_value:
        isIncomeDeferred || variant !== 'eligible' ? null : 'confirmed',
      gates_eligibility: true,
      key: 'income_min',
      listing_value: '3.0 × warm rent = € 3.750',
      provenance:
        isIncomeDeferred || variant !== 'eligible'
          ? null
          : { at: '2026-09-01T10:05:00Z', source: 'applicant' },
      reason: isIncomeDeferred ? 'deferred_to_screening' : null,
      s: null,
      status: isIncomeDeferred
        ? 'not_applicable'
        : variant === 'eligible'
          ? 'fits'
          : 'not_provided',
      value_type: 'enum',
      weight: 0,
    },
  ]
}

const MOCK_MATCH_SUGGESTED_VALUE = {
  intended_duration: 24,
  max_budget_warm: 1380,
  needs: ['balcony'],
  rooms_area: '≥ 2 rooms',
}

function getMockMatchSuggestions(criteria) {
  if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.matchNoSuggestions)) {
    return null
  }

  return criteria
    .filter(
      criterion =>
        criterion.applicant_value == null &&
        MOCK_MATCH_SUGGESTED_VALUE[criterion.applicant_field]
    )
    .map(criterion => ({
      at: '2026-09-01T08:20:00Z',
      field: criterion.applicant_field,
      source: 'greta_message',
      status:
        criterion.status === MATCH_CRITERION_NOT_APPLICABLE
          ? 'deferred_phase_b'
          : 'pending',
      value: MOCK_MATCH_SUGGESTED_VALUE[criterion.applicant_field],
    }))
}

export function getMockMatchUnavailableReason() {
  return hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.matchUnavailable)
    ? 'listing_missing_availability'
    : null
}

function getMockMatchVariant(applicant) {
  if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.matchNotEvaluated)) {
    return null
  }

  return ['eligible', 'incomplete', 'not_eligible'][Number(applicant.id) % 3]
}

const MATCH_ACCEPTED_STATUS = 'fits'

const MATCH_CRITERION_NOT_APPLICABLE = 'not_applicable'

const MOCK_MATCH_SUGGESTION_RESOLUTIONS = new Map()

function getMockMatchResolutions(applicantId) {
  if (!MOCK_MATCH_SUGGESTION_RESOLUTIONS.has(applicantId)) {
    MOCK_MATCH_SUGGESTION_RESOLUTIONS.set(applicantId, {
      accepted: {},
      dismissed: [],
    })
  }

  return MOCK_MATCH_SUGGESTION_RESOLUTIONS.get(applicantId)
}

const MOCK_MATCH_STATUS_RULE = {
  household_size: (value, listingValue) =>
    Number(value) <= Number(listingValue) ? 'fits' : 'does_not_fit',
  intended_duration: (value, listingValue) =>
    Number(value) >= Number(listingValue) ? 'fits' : 'does_not_fit',
  max_budget_warm: (value, listingValue) =>
    Number(value) >= Number(listingValue) ? 'fits' : 'partial',
  move_in_date: (value, listingValue) =>
    value >= listingValue ? 'fits' : 'partial',
}

function getMockMatchStatusForValue(criterion, value) {
  const rule = MOCK_MATCH_STATUS_RULE[criterion.applicant_field]

  return rule ? rule(value, criterion.listing_value) : MATCH_ACCEPTED_STATUS
}

const MOCK_MATCH_REQUEST_SENT_AT = new Map()

const MOCK_MATCH_REQUEST_SENT = []

export function getMockApplicantMatchRequests() {
  return MOCK_MATCH_REQUEST_SENT
}

export function postMockApplicantMatchRequestInfo({
  applicantId,
  informationTextVersion,
}) {
  MOCK_MATCH_REQUEST_SENT_AT.set(Number(applicantId), new Date().toISOString())

  MOCK_MATCH_REQUEST_SENT.push({
    applicantId: Number(applicantId),
    information_text_version: informationTextVersion,
  })

  return Promise.resolve({ data: null })
}

export function patchMockApplicantMatchingValue({ applicantId, payload }) {
  const [field] = Object.keys(payload)

  getMockMatchResolutions(Number(applicantId)).accepted[field] = payload[field]

  return Promise.resolve({ data: null })
}

export function postMockApplicantMatchSuggestionAccept({
  applicantId,
  field,
  value,
}) {
  getMockMatchResolutions(Number(applicantId)).accepted[field] = value

  return Promise.resolve({ data: null })
}

export function postMockApplicantMatchReevaluate({ applicantId }) {
  MOCK_MATCH_STALE_SINCE.set(
    Number(applicantId),
    Date.now() - MOCK_MATCH_STALE_DURATION
  )

  return Promise.resolve({ data: null })
}

export function postMockApplicantMatchSuggestionDismiss({
  applicantId,
  field,
}) {
  getMockMatchResolutions(Number(applicantId)).dismissed.push(field)

  return Promise.resolve({ data: null })
}

function getMockMatchEligibility(criteria) {
  const blocking = criteria.find(
    criterion =>
      criterion.gates_eligibility && criterion.status === 'does_not_fit'
  )

  if (blocking) {
    return {
      eligibility: 'not_eligible',
      eligibility_reasons: [blocking.reason].filter(Boolean),
    }
  }

  const isIncomplete = criteria.some(
    criterion =>
      criterion.gates_eligibility && criterion.status === 'not_provided'
  )

  return isIncomplete
    ? {
        eligibility: 'incomplete',
        eligibility_reasons: ['missing_applicant_data'],
      }
    : { eligibility: 'eligible', eligibility_reasons: [] }
}

// TODO: read #1 does not exist yet
const MOCK_MATCH_STATUS_SCORE = {
  does_not_fit: 0,
  fits: 1,
  partial: 0.5,
}

const MOCK_MATCH_SCORE_MIN_COUNT = 3

const MOCK_MATCH_SCORE_MIN_COVERAGE = 0.5

const MOCK_MATCH_TOTAL_WEIGHT = Object.values(MOCK_MATCH_WEIGHTS.raw).reduce(
  (total, weight) => total + weight,
  0
)

function getMockMatchBreakdown(criteria) {
  return criteria
    .filter(criterion => criterion.weight > 0 && criterion.s != null)
    .map(({ key, s, weight }) => ({
      key,
      s,
      w: weight,
      w_pct: weight / MOCK_MATCH_TOTAL_WEIGHT,
      ws: weight * s,
    }))
}

function getMockMatchBreakdownExcluded(criteria) {
  return criteria
    .filter(criterion => criterion.weight > 0 && criterion.s == null)
    .map(({ key, weight }) => ({ key, w: weight }))
}

const MOCK_MATCH_SCORE_FORMULA_DIGITS = 4

function getMockMatchScoreFormula({ breakdown, score, weightPresent }) {
  const contribution = breakdown.reduce((total, item) => total + item.ws, 0)

  return `Σ w·s / Σ w present = ${contribution.toFixed(3)} / ${weightPresent} = ${score.toFixed(MOCK_MATCH_SCORE_FORMULA_DIGITS)} → ${Math.round(score * 100)} %`
}

function getMockMatchScoreFields({ criteria, eligibility }) {
  const breakdown = getMockMatchBreakdown(criteria)

  const weightPresent = breakdown.reduce((total, item) => total + item.w, 0)

  const coverage = weightPresent / MOCK_MATCH_TOTAL_WEIGHT

  if (eligibility === 'not_eligible') {
    return { coverage: null, score_reason: 'not_eligible' }
  }

  if (
    breakdown.length < MOCK_MATCH_SCORE_MIN_COUNT ||
    coverage < MOCK_MATCH_SCORE_MIN_COVERAGE
  ) {
    return { coverage: null, score_reason: 'insufficient_coverage' }
  }

  const score =
    breakdown.reduce((total, item) => total + item.ws, 0) / weightPresent

  return {
    breakdown,
    breakdown_excluded: getMockMatchBreakdownExcluded(criteria),
    coverage,
    score,
    score_formula: getMockMatchScoreFormula({
      breakdown,
      score,
      weightPresent,
    }),
    score_reason: 'ok',
  }
}

function applyMockMatchResolutions({ applicantId, match }) {
  const { accepted, dismissed } = getMockMatchResolutions(Number(applicantId))

  const resolved = [...Object.keys(accepted), ...dismissed]

  const criteria = match.criteria.map(criterion =>
    Object.hasOwn(accepted, criterion.applicant_field)
      ? {
          ...criterion,
          applicant_value: accepted[criterion.applicant_field],
          provenance: {
            at: new Date().toISOString(),
            source: 'confirmed',
          },
          reason: null,
          s: MOCK_MATCH_STATUS_SCORE[
            getMockMatchStatusForValue(
              criterion,
              accepted[criterion.applicant_field]
            )
          ],
          status: getMockMatchStatusForValue(
            criterion,
            accepted[criterion.applicant_field]
          ),
        }
      : criterion
  )

  const eligibility = getMockMatchEligibility(criteria)

  return {
    ...match,
    ...eligibility,
    ...getMockMatchScoreFields({
      criteria,
      eligibility: eligibility.eligibility,
    }),
    criteria,
    provided_count: criteria.filter(item => item.applicant_value != null)
      .length,
    suggestions:
      match.suggestions?.filter(item => !resolved.includes(item.field)) ?? null,
  }
}

function getIsMockMatchStale(applicantId) {
  if (!hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.matchStale)) {
    return false
  }

  const staleSince = MOCK_MATCH_STALE_SINCE.get(Number(applicantId))

  if (!staleSince) {
    MOCK_MATCH_STALE_SINCE.set(Number(applicantId), Date.now())

    return true
  }

  return Date.now() - staleSince < MOCK_MATCH_STALE_DURATION
}

export function getMockApplicantMatch(applicant) {
  const unavailableReason = getMockMatchUnavailableReason()

  if (unavailableReason) {
    return { unavailable_reason: unavailableReason }
  }

  const variant = getMockMatchVariant(applicant)

  if (!variant) {
    return null
  }

  const criteria = getMockMatchCriteria({
    id: applicant.id,
    stage: applicant.stage,
    variant,
  })

  const base = {
    can_configure_matching: true,
    can_request_from_applicant: !hasRentalPortalMockState(
      RENTAL_PORTAL_MOCK_STATE.matchNoRequestRight
    ),
    criteria,
    criteria_count: criteria.length,
    currency: 'EUR',
    eligibility: variant,
    eligibility_reasons:
      variant === 'not_eligible'
        ? ['household_exceeds_max']
        : variant === 'incomplete'
          ? ['missing_applicant_data']
          : [],
    evaluated_at: '2026-09-02T12:12:00Z',
    information_text: getMockMatchInformationText(),
    last_request_sent_at:
      MOCK_MATCH_REQUEST_SENT_AT.get(Number(applicant.id)) ??
      (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.matchRequestThrottled)
        ? new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString()
        : null),
    provided_count: criteria.filter(item => item.applicant_value != null)
      .length,
    ruleset_version: '1.0',
    stale: getIsMockMatchStale(applicant.id),
    suggestions: getMockMatchSuggestions(criteria),
    weights_used: MOCK_MATCH_WEIGHTS,
  }

  return applyMockMatchResolutions({ applicantId: applicant.id, match: base })
}

const MOCK_MATCH_LIST_OMITTED_FIELDS = [
  'breakdown',
  'suggestions',
  'weights_used',
]

export function getMockApplicantListMatch(applicant) {
  const match = getMockApplicantMatch(applicant)

  return match ? omit(match, MOCK_MATCH_LIST_OMITTED_FIELDS) : null
}
